import type { requireSuperBatchFrontendResourceRenderQuery$data } from './__generated__/requireSuperBatchFrontendResourceRenderQuery.graphql.ts';

type QueryResource = NonNullable<
	requireSuperBatchFrontendResourceRenderQuery$data['internalFrontendResource']
>['resourceList'][number];

export type SuperbatchResource = Omit<QueryResource, 'type' | 'url' | 'attributes'> & {
	type: 'js' | 'css';
	url: string;
	attributes: ReadonlyArray<{ key: string; value: string }>;
};

export class SuperbatchResourceError extends Error {
	constructor(readonly reason: 'graphql_error' | 'invalid_resources' | 'missing_bootstrap') {
		super(`Superbatch unavailable: ${reason}`);
		this.name = 'SuperbatchResourceError';
	}
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

function isResource(value: unknown): value is SuperbatchResource {
	return (
		isRecord(value) &&
		(value.type === 'js' || value.type === 'css') &&
		typeof value.url === 'string' &&
		value.url.trim().length > 0 &&
		Array.isArray(value.attributes) &&
		value.attributes.every(
			(attribute: unknown) =>
				isRecord(attribute) &&
				typeof attribute.key === 'string' &&
				typeof attribute.value === 'string',
		)
	);
}

/** Validate the raw GraphQL envelope before timing markers can disguise an empty response. */
export function validateSuperbatchResources(response: unknown): SuperbatchResource[] {
	if (!isRecord(response)) {
		throw new SuperbatchResourceError('invalid_resources');
	}
	if (
		response.errors !== undefined &&
		(!Array.isArray(response.errors) || response.errors.length)
	) {
		throw new SuperbatchResourceError('graphql_error');
	}
	const data = response.data;
	const frontendResource = isRecord(data) ? data.internalFrontendResource : undefined;
	const resources = isRecord(frontendResource) ? frontendResource.resourceList : undefined;
	if (!Array.isArray(resources)) {
		throw new SuperbatchResourceError('invalid_resources');
	}
	const normalizedResources: SuperbatchResource[] = [];
	const seenResourceIds = new Set<string>();
	for (const resource of resources) {
		if (!isRecord(resource) || typeof resource.type !== 'string') {
			throw new SuperbatchResourceError('invalid_resources');
		}
		// The loader only consumes JS and CSS; other resource types do not block the bootstrap.
		if (resource.type !== 'js' && resource.type !== 'css') {
			continue;
		}
		const attributes = resource.attributes ?? [];
		const normalizedResource = {
			...resource,
			attributes: Array.isArray(attributes)
				? attributes
				: isRecord(attributes)
					? Object.entries(attributes).map(([key, value]) => ({ key, value }))
					: attributes,
		};
		if (!isResource(normalizedResource)) {
			throw new SuperbatchResourceError('invalid_resources');
		}
		// Identical entries must not emit the same executable tags twice in SSR either.
		const resourceIdentity = JSON.stringify([
			normalizedResource.type,
			normalizedResource.url,
			[...normalizedResource.attributes].sort((a, b) => a.key.localeCompare(b.key)),
		]);
		if (!seenResourceIds.has(resourceIdentity)) {
			seenResourceIds.add(resourceIdentity);
			normalizedResources.push(normalizedResource);
		}
	}
	if (
		!normalizedResources.some(
			(resource) =>
				resource.type === 'js' &&
				resource.attributes.some(
					({ key, value }) => key === 'data-wrm-key' && value === '_super',
				) &&
				resource.attributes.some(
					({ key, value }) => key === 'data-wrm-batch-type' && value === 'context',
				),
		)
	) {
		throw new SuperbatchResourceError('missing_bootstrap');
	}
	return normalizedResources;
}
